import { useMemo, useState } from "react";

// First-use welcome gate persistence. The "what to expect" screen is meant to
// orient a visitor the first time they open a given Genie — once they've
// entered it on this device, we never show it again for that Genie. Keyed
// per-Genie so each new Genie a visitor tries gets its own one-time welcome.
// localStorage is per-browser (anonymous consumers have no cross-device
// identity), wrapped so a privacy/quota error can never break the page.
//
// This key scheme is shared with the legacy LiveAgentPage — dismissing the
// welcome on either surface counts as seen on both, since it's the same
// underlying genie/visitor.
const welcomeSeenKey = (genieId: string) => `genie-welcome-seen:${genieId}`;

const hasSeenGenieWelcome = (genieId: string) => {
  try {
    return localStorage.getItem(welcomeSeenKey(genieId)) === "1";
  } catch {
    return false;
  }
};

const markGenieWelcomeSeen = (genieId: string) => {
  try {
    localStorage.setItem(welcomeSeenKey(genieId), "1");
  } catch {
    /* storage unavailable — the welcome simply shows again next visit */
  }
};

export interface WelcomeGate {
  /** Session toggle: stays true until the visitor enters (or we learn
   * they've already seen it for this Genie). */
  showWelcome: boolean;
  setShowWelcome: (value: boolean) => void;
  /** First-use only: true if this browser has already entered THIS Genie
   * before. Derived from agentId so returning visitors get no welcome flash. */
  welcomeSeenForGenie: boolean;
  /** Dismiss the gate and remember it for this Genie so it's first-use only. */
  dismissWelcome: (genieId: string | undefined | null) => void;
}

export function useWelcomeGate(agentId: string | undefined | null): WelcomeGate {
  const [showWelcome, setShowWelcome] = useState(true);

  const welcomeSeenForGenie = useMemo(
    () => (agentId ? hasSeenGenieWelcome(agentId) : false),
    [agentId],
  );

  const dismissWelcome = (genieId: string | undefined | null) => {
    if (genieId) markGenieWelcomeSeen(genieId);
    setShowWelcome(false);
  };

  return { showWelcome, setShowWelcome, welcomeSeenForGenie, dismissWelcome };
}
